import { useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  CssBaseline,
  IconButton,
  LinearProgress,
  Snackbar,
  Stack,
  ThemeProvider,
  Tooltip,
  Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid/DataGrid';

import themev5 from '../../themev5';
import DatagridNoRowsOverlay from '../Datagrid/DatagridNoRowsOverlay';
import datagridStyles from '../Datagrid/Datagrid.styles';
import getSnapshotComparisonDetailColumns from './detailColumns';
import SnapshotComparisonDetailFilters from './components/SnapshotComparisonDetailFilters';
import SnapshotComparisonCompareModal from './components/SnapshotComparisonCompareModal';
import SnapshotComparisonLetterModal from './components/SnapshotComparisonLetterModal';
import useSnapshotComparisonDetail from './hooks/useSnapshotComparisonDetail';
import useSnapshotComparisonComparePdfs from './hooks/useSnapshotComparisonComparePdfs';
import useSnapshotComparisonLetter from './hooks/useSnapshotComparisonLetter';
import useClearSnapshotComparisonListStateOnLeave from './hooks/useClearSnapshotComparisonListStateOnLeave';
import { COMPARISON_COLUMN_WIDTH } from './constants';
import styles from './styles';

const SnapshotComparisonDetail = () => {
  const history = useHistory();
  useClearSnapshotComparisonListStateOnLeave();
  const { applicationNumber: applicationNumberParam } = useParams<{ applicationNumber: string }>();
  const applicationNumber = applicationNumberParam?.trim() ?? '';

  useEffect(() => {
    // Guard invalid routes like /snapshot-comparison/ or /snapshot-comparison/undefined.
    if (!applicationNumber) {
      history.replace('/snapshot-comparison');
    }
  }, [applicationNumber, history]);

  const {
    rows,
    loading,
    error,
    savingRowId,
    feedback,
    summary,
    filters,
    indChangeOptions,
    tierOptions,
    sectionOptions,
    setVerdictFilter,
    setIndChangeFilter,
    setTierFilter,
    clearFilters,
    updateRowField,
    saveRow,
    clearFeedback
  } = useSnapshotComparisonDetail(applicationNumber);

  const comparePdfs = useSnapshotComparisonComparePdfs(applicationNumber);
  const letterViewer = useSnapshotComparisonLetter();

  const columns = useMemo(
    () =>
      getSnapshotComparisonDetailColumns({
        onVerdictChange: (id, value) => updateRowField(id, 'sme_verdict', value),
        onCommentsChange: (id, value) => updateRowField(id, 'sme_comments', value),
        onCompare: comparePdfs.openCompare,
        onOpenLetter: letterViewer.openLetter,
        onSave: saveRow,
        savingRowId
      }),
    [updateRowField, saveRow, savingRowId, comparePdfs.openCompare, letterViewer.openLetter]
  );

  if (!applicationNumber) {
    return null;
  }

  return (
    <ThemeProvider theme={themev5}>
      <CssBaseline />
      <Box sx={styles.page}>
        <Stack direction='row' alignItems='center' spacing={1}>
          <Tooltip title='Back'>
            <IconButton onClick={() => history.push('/snapshot-comparison')} size='small'>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant='h5'>Application {applicationNumber}</Typography>
        </Stack>

        <Typography variant='body2' color='text.secondary' sx={styles.summary}>
          {summary.total === summary.allTotal
            ? `${summary.total} comparisons · ${summary.yes} YES · ${summary.no} NO`
            : `${summary.total} of ${summary.allTotal} comparisons · ${summary.yes} YES · ${summary.no} NO`}
        </Typography>

        <SnapshotComparisonDetailFilters
          filters={filters}
          indChangeOptions={indChangeOptions}
          tierOptions={tierOptions}
          onVerdictChange={setVerdictFilter}
          onIndChangeChange={setIndChangeFilter}
          onTierChange={setTierFilter}
          onClear={clearFilters}
        />

        {error && (
          <Box sx={styles.errorContainer}>
            <Alert severity='error'>{error}</Alert>
          </Box>
        )}

        <Box sx={styles.tableContainer}>
          <Box sx={[styles.pinnedComparisonHeader, { width: COMPARISON_COLUMN_WIDTH, height: 56 }]}>
            Comparison
          </Box>
          <DataGrid
            key={applicationNumber}
            // Remount grid when switching applications so pagination resets to page 1.
            disableVirtualization
            sx={[
              datagridStyles.datagrid,
              styles.pinnedComparisonGrid,
              { '& .comparison-cell': styles.comparisonCell }
            ]}
            rows={rows}
            columns={columns}
            getRowId={row => row.id}
            rowHeight={140}
            loading={loading}
            disableSelectionOnClick
            checkboxSelection={false}
            rowsPerPageOptions={[10, 20, 50, 100]}
            initialState={{ pagination: { pageSize: 20 } }}
            components={{
              LoadingOverlay: LinearProgress,
              NoRowsOverlay: DatagridNoRowsOverlay
            }}
          />
        </Box>
      </Box>

      <SnapshotComparisonCompareModal
        open={comparePdfs.open}
        onClose={comparePdfs.closeCompare}
        applicationNumber={applicationNumber}
        comparedToSubmission={comparePdfs.selectedRow?.compared_to_submission ?? null}
        currentSubmission={comparePdfs.selectedRow?.submission ?? null}
        previousPdfUrl={comparePdfs.previousPdfUrl}
        previousPdfLoading={comparePdfs.previousPdfLoading}
        previousPdfError={comparePdfs.previousPdfError}
        currentPdfUrl={comparePdfs.currentPdfUrl}
        currentPdfLoading={comparePdfs.currentPdfLoading}
        currentPdfError={comparePdfs.currentPdfError}
        pdfViewMode={comparePdfs.pdfViewMode}
        onPdfViewModeChange={comparePdfs.changePdfViewMode}
      />

      <SnapshotComparisonLetterModal
        open={letterViewer.open}
        onClose={letterViewer.closeLetter}
        onConfirm={sections => {
          if (letterViewer.selectedRow) {
            updateRowField(letterViewer.selectedRow.id, 'section_changed', sections);
          }
          letterViewer.closeLetter();
        }}
        submission={letterViewer.selectedRow?.submission ?? null}
        letterIndex={letterViewer.letterIndex}
        letterCount={letterViewer.letterCount}
        pdfUrl={letterViewer.pdfUrl}
        loading={letterViewer.loading}
        error={letterViewer.error}
        canGoPrevious={letterViewer.canGoPrevious}
        canGoNext={letterViewer.canGoNext}
        onPrevious={letterViewer.goToPrevious}
        onNext={letterViewer.goToNext}
        sections={letterViewer.selectedRow?.section_changed ?? []}
        sectionOptions={sectionOptions}
      />

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={4000}
        onClose={clearFeedback}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {feedback ? (
          <Alert onClose={clearFeedback} severity={feedback.severity} sx={styles.snackbarAlert}>
            {feedback.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ThemeProvider>
  );
};

export default SnapshotComparisonDetail;
